import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { CrmService } from '../../crm/crm.service';
import { OrdersService } from '../../order/order.service';
import { PaymentTerm } from '../../order/models/payment-term.model';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { Shipment } from '../models/shipment';
import { ShipmentCommercialPartyCorrectionRequest, ShipmentService } from '../shipment.service';

@Component({
    selector: 'app-shipment-commercial-party-correction',
    standalone: true,
    templateUrl: './shipment-commercial-party-correction.component.html',
    styleUrls: ['../../shared/party-quick-create/party-quick-create-dialog.component.css',
        './shipment-commercial-party-correction.component.css'],
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule,
        IftaLabelModule, InputGroupModule, InputGroupAddonModule, TextareaModule, CommercialPartyPickerComponent]
})
export class ShipmentCommercialPartyCorrectionComponent {
    @Input() shipment?: Shipment;
    @Output() corrected = new EventEmitter<void>();

    visible = false;
    saving = false;
    reason = '';
    reviewed = false;
    error = '';
    target: CommercialPartyReference = { source: 'CRM' };
    defaultPaymentTermId?: number;
    paymentTerms: PaymentTerm[] = [];
    private expected?: ShipmentCommercialPartyCorrectionRequest['expected'];

    constructor(private shipmentService: ShipmentService, private crmService: CrmService,
        private orderService: OrdersService) {}

    open(): void {
        if (!this.shipment?.shipmentId || !this.shipment.owningCompany?.id) return;
        const shipment = this.shipment;
        this.expected = {
            crmClientId: shipment.crmClientId ?? null,
            crmAccountId: shipment.crmAccountId ?? null,
            crmContactId: shipment.crmClientContactId ?? null,
            legacyCompanyId: shipment.company?.id ?? null,
            legacyCustomerId: shipment.customer?.id ?? null
        };
        this.target = {
            source: 'CRM',
            crmClientId: shipment.crmClientId ?? null,
            crmAccountId: shipment.crmAccountId ?? null,
            crmContactId: shipment.crmClientContactId ?? null
        };
        this.reason = '';
        this.reviewed = false;
        this.error = '';
        this.defaultPaymentTermId = undefined;
        this.visible = true;
        this.orderService.getAllPaymentTerms(shipment.owningCompany.id).subscribe({
            next: terms => this.paymentTerms = terms,
            error: () => this.paymentTerms = []
        });
        this.loadDefaultTerm(this.target.crmClientId);
    }

    onPartyChange(target: CommercialPartyReference): void {
        const previousClientId = this.target.crmClientId;
        this.target = target;
        this.error = '';
        this.reviewed = false;
        if (target.crmClientId !== previousClientId) this.loadDefaultTerm(target.crmClientId);
    }

    get defaultPaymentTermLabel(): string {
        const id = this.defaultPaymentTermId;
        return id ? this.paymentTerms.find(term => term.paymentTermID === id)?.name || `Term #${id}`
            : 'No default term';
    }

    get canSave(): boolean {
        return !this.saving && !!this.target.crmClientId && !!this.reason.trim() && this.reviewed
            && !!this.expected && (this.target.crmClientId !== this.expected.crmClientId
                || (this.target.crmAccountId ?? null) !== this.expected.crmAccountId
                || (this.target.crmContactId ?? null) !== this.expected.crmContactId);
    }

    save(): void {
        if (!this.canSave || !this.shipment?.shipmentId || !this.expected) return;
        this.saving = true;
        this.error = '';
        this.shipmentService.correctCommercialParty(this.shipment.shipmentId, {
            target: {
                source: 'CRM',
                crmClientId: this.target.crmClientId!,
                crmAccountId: this.target.crmAccountId ?? null,
                crmContactId: this.target.crmContactId ?? null
            },
            expected: this.expected,
            reason: this.reason.trim(),
            confirmPreserveCommercials: this.reviewed
        }).pipe(finalize(() => this.saving = false)).subscribe({
            next: () => {
                this.visible = false;
                this.corrected.emit();
            },
            error: error => this.error = error?.error?.detail || error?.error?.message
                || error?.message || 'The bill-to party could not be corrected.'
        });
    }

    private loadDefaultTerm(clientId?: number | null): void {
        this.defaultPaymentTermId = undefined;
        if (!clientId) return;
        this.crmService.getClient(clientId).subscribe({
            next: response => {
                if (this.target.crmClientId === clientId) {
                    this.defaultPaymentTermId = response.data?.defaultPaymentTermId;
                }
            },
            error: () => {
                if (this.target.crmClientId === clientId) this.error = 'Could not load the selected client’s default payment term.';
            }
        });
    }
}
